import { DeliveryPromise } from "@omni/types/api/cart/bff/shared/deliveryPromise.model";
import { DeliveryPromiseType } from "@omni/types/api/product/bff/shared/enums/deliveryPromiseType.enum";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { CloudDeliveryPromiseType } from "@omni/types/api/product/cloud/shared/enums/deliveryPromiseType.enum";
import { CloudStockLevel } from "@omni/types/api/product/cloud/shared/enums/stockLevel.enum";

import { hasNoStock } from "./productAvailability";

export const getHomeDeliveryKey = (subject: string) => {
    return `${subject}.deliveryPromise.homeDelivery`;
};

export const determineDeliveryTimeTranslationKey = ({
    deliveryPromiseType,
    stockLevel,
    realTimeStockAvailable,
    realTimeStock,
    deliveryTime,
    isDriveIn
}: {
    deliveryPromiseType?: CloudDeliveryPromiseType;
    stockLevel?: CloudStockLevel | null;
    realTimeStockAvailable?: boolean;
    realTimeStock?: number;
    deliveryTime?: number;
    isDriveIn?: boolean;
}): undefined | string => {
    const hasDeliveryTime = !!deliveryTime;
    const deliveryTypeKey = isDriveIn ? "driveInPickUp" : "clickAndCollect";

    if (deliveryPromiseType === CloudDeliveryPromiseType.AvailableForS2S) {
        return hasDeliveryTime
            ? "pdp.deliveryPromise.shipToStore"
            : "pdp.deliveryPromise.shipToStore.noDeliveryTime";
    }

    if (hasNoStock(realTimeStockAvailable, stockLevel, realTimeStock)) {
        return isDriveIn
            ? "pdp.deliveryPromise.noStock.driveInPickUp"
            : "pdp.deliveryPromise.noStock";
    }

    if (
        deliveryPromiseType === CloudDeliveryPromiseType.OnlyAvailableOffline &&
        realTimeStockAvailable
    ) {
        return "pdp.deliveryPromise.onlyAvailableOffline.realTimeStock";
    }

    if (realTimeStockAvailable && !isDriveIn && !hasDeliveryTime) {
        return "pdp.deliveryPromise.clickAndCollect.realTimeStock.noDeliveryTime";
    }

    if (realTimeStockAvailable) {
        return `pdp.deliveryPromise.${deliveryTypeKey}.realTimeStock`;
    }

    if (deliveryPromiseType === CloudDeliveryPromiseType.OnlyAvailableOffline) {
        return "pdp.deliveryPromise.onlyAvailableOffline";
    }

    if (
        deliveryPromiseType !==
        CloudDeliveryPromiseType.AvailableForPickUpInStore
    ) {
        return;
    }

    if (
        !isDriveIn &&
        !hasDeliveryTime &&
        stockLevel === CloudStockLevel.LowStock
    ) {
        return "pdp.deliveryPromise.clickAndCollect.lowStock.noDeliveryTime";
    }

    if (!isDriveIn && !hasDeliveryTime) {
        return "pdp.deliveryPromise.clickAndCollect.default.noDeliveryTime";
    }

    if (stockLevel === CloudStockLevel.LowStock) {
        return `pdp.deliveryPromise.${deliveryTypeKey}.lowStock`;
    }

    return `pdp.deliveryPromise.${deliveryTypeKey}.default`;
};

export const cartDeliveryPromiseTranslationKey = ({
    deliveryPromise,
    deliveryType,
    withRemove
}: {
    deliveryPromise?: DeliveryPromise | null;
    deliveryType?: DeliveryType | null;
    withRemove?: boolean;
} = {}): undefined | string => {
    const hasDeliveryTime = !!deliveryPromise?.deliveryTime;

    if (!deliveryPromise) {
        return "cart.deliveryPromise.noStock";
    }

    if (deliveryPromise.deliveryPromiseType === DeliveryPromiseType.NoStock) {
        return withRemove
            ? "cart.deliveryPromise.noStockAndRemove"
            : "cart.deliveryPromise.noStock";
    }

    if (deliveryType === DeliveryType.ClickAndCollect) {
        return hasDeliveryTime
            ? "cart.deliveryPromise.clickAndCollect.default"
            : undefined;
    }

    if (deliveryType === DeliveryType.HomeDelivery) {
        return hasDeliveryTime ? getHomeDeliveryKey("cart") : undefined;
    }

    return "cart.deliveryPromise.noStock";
};
